import type { Point2d } from "@logic/lib/2d"
import { getReorientedPanel, foreshortenIsomorphic } from "@logic/lib/2d"
import type { Node2d } from "@logic/lib/mesh"
import { selector, DefaultValue } from "recoil"

import {
  filteredPanelSelectionState,
  findPanelState,
  panelSelectionState,
} from "."
import { phaseState } from "../../phase"
import type { UnknownChangeForUnknownElement } from "../timeline"
import { divergeTimeline, generateElementChange } from "../timeline/transactions"
import type { TransactionOperation } from "../transactions"
import { findCenterOfSelectedPanels } from "../transactions/findCenterOfSelectedPanels"
import { getAzimuthFromPanelArea } from "../transactions/getAzimuthFromPanel"
import { getPanelFootprint } from "../transactions/getPanelFootprint"
import { getSlopeFromPanelArea } from "../transactions/getSlopeFromPanel"

type SelectionRotationOptions = {
  origin: Point2d
  displacement: Point2d
  willReorient90: boolean
  shouldSnap: { x: boolean; y: boolean }
  offset: Node2d
  dragging: boolean
}

export const reorientPanels: TransactionOperation<
  Partial<SelectionRotationOptions>
> = (transactors, options) => {
  const { get, set } = transactors
  const currentPhase = get(phaseState)
  if (currentPhase !== `Panel Placement`) return

  const {
    origin = findCenterOfSelectedPanels(transactors, { debug: false }),
    displacement = { x: 0, y: 0 },
    willReorient90 = false,
    shouldSnap = { x: false, y: false },
    offset = { x: 0, y: 0 },
    dragging = false,
  } = options

  const changes: UnknownChangeForUnknownElement[] = []
  const selection = dragging
    ? get(filteredPanelSelectionState)
    : get(panelSelectionState)

  selection.forEach((panelId) => {
    const panelState = findPanelState(panelId)
    const panel = get(panelState)
    const azimuth = getAzimuthFromPanelArea(transactors, panelId)
    const slope = getSlopeFromPanelArea(transactors, panelId)
    const rotatedPanel = getReorientedPanel({
      panel,
      origin,
      azimuth,
      addedRotation: willReorient90 ? 90 : 0,
    })
    const isLandscape =
      rotatedPanel.orientation === 90 || rotatedPanel.orientation === 270

    const moduleScale = getPanelFootprint(transactors, panel)
    const moduleScaleInPerspective = foreshortenIsomorphic(
      slope,
      isLandscape ? `x` : `y`,
    ).box({ ...rotatedPanel, ...moduleScale }, origin)

    set(panelState, {
      ...rotatedPanel,
      ...moduleScaleInPerspective,
      x: rotatedPanel.x + displacement.x,
      y: rotatedPanel.y + displacement.y,
      xSnap: shouldSnap.x ? rotatedPanel.x + offset.x : null,
      ySnap: shouldSnap.y ? rotatedPanel.y + offset.y : null,
    })

    if (willReorient90) {
      changes.push(
        generateElementChange(transactors, {
          action: {
            type: `UPDATE`,
            family: `PANEL`,
            id: panelId,
            payload: rotatedPanel,
          },
        }),
      )
    }
  })

  if (willReorient90) {
    divergeTimeline(transactors, changes)
  }
}

export const panelSelectionRotationState = selector<
  Partial<SelectionRotationOptions>
>({
  key: `rotatePanelStateSelection`,
  get: () => {
    return {
      origin: { x: NaN, y: NaN },
      displacement: { x: NaN, y: NaN },
      willReorient90: false,
      shouldSnap: { x: false, y: false },
      offset: { x: NaN, y: NaN },
    }
  },
  set: (transactors, newValues) =>
    newValues instanceof DefaultValue || reorientPanels(transactors, newValues),
})
